import {beansprout, mash} from "@/store/Services";
import {AccountUser, CompetitorOnlineProfile, OnlineProfileStats} from "@/app/utils/types";
import profilesModule from "@/store/vuex/modules/ProfilesModule";
import VuexStore from "@/store/vuex/VuexStore";
import asd from "@/store/vuex/modules/CompetitorProfileHelper";

let refreshCompetitorProfilesPromise = null;
let refreshProfilesLatestPromise = null;

/**
 * <p>This is a module for managing competitor profiles.</p>
 * <p>Competitor profiles will be referred to as profiles or onlineProfiles, regardless, they are in fact competitor profiles</p>
 * <p>This store is also the primary method of calling Beansprout API's</p>
 *
 */
export default {
    namespaced: true,

    state: () => ({
        /**
         * Changes in OnlineProfileStats for competitor online profiles for the account over time.
         *
         * @type {OnlineProfileStats[]}
         */
        profilesTimeline: null,

        /**
         * The latest OnlineProfileStats for competitor online profiles for the account.
         *
         * @type {OnlineProfileStats[]}
         */
        latestProfilesStats: null,

        /**
         * The competitor OnlineProfiles for the account.
         *
         * @type {CompetitorOnlineProfile[]}
         */
        competitorProfiles: null,

        /**
         * The competitor OnlineProfiles for the account.
         *
         * @type {CompetitorOnlineProfile[]}
         */
        ownProfiles: null
    }),

    mutations: {
        setLatestProfilesStats: (state, value) => {
            value.forEach(profileStat => profileStat.__proto__ = new OnlineProfileStats())
            state.latestProfilesStats = value
        },

        setCompetitorProfiles: (state, value) => {
            value.forEach(profileStat => profileStat.__proto__ = new CompetitorOnlineProfile())
            state.competitorProfiles = value
        },

        setOwnProfiles: (state, value) => {
            value.forEach(profileStat => profileStat.__proto__ = new CompetitorOnlineProfile())
            state.ownProfiles = value
        },
    },

    getters: {
        getCompetitorProfiles: (state) => {
            if (!state.competitorProfiles) return [];

            return state.competitorProfiles
        },

        getOwnProfiles: (state) => {
            if (!state.ownProfiles) return [];

            return state.ownProfiles
        },

        getLatestCompetitorProfilesWithStats: (state) => {
            if (!state.latestProfilesStats) return [];

            return state.latestProfilesStats
        }
    },

    actions: {
        async refreshCompetitorProfiles({state, commit, rootState}, forceRefresh) {
            forceRefresh ??= false;

            if (state.competitorProfiles && !forceRefresh) return;

            if (refreshCompetitorProfilesPromise) {
                await refreshCompetitorProfilesPromise;
                return;
            }

            refreshCompetitorProfilesPromise = (async () => {
                try {
                    const onlineProfiles =
                        await mash.get("/rest/accounts/" + rootState.account.code + "/brands?includeStats=true");

                    const competitorOnlineProfileIds = onlineProfiles.data
                        .filter(onlineProfile => onlineProfile !== null && onlineProfile.category === "COMPETITOR")
                        .flatMap(onlineProfile => {
                            const competitorBrandIdsForAcc = [
                                ...(onlineProfile.otherProfileIds || []),
                                ...(onlineProfile.supportProfileIds || [])
                            ];

                            return competitorBrandIdsForAcc;
                        });

                    const ownOnlineProfileIds = onlineProfiles.data
                        .filter(onlineProfile => onlineProfile !== null && onlineProfile.category === "OWN")
                        .flatMap(onlineProfile => {
                            const competitorBrandIdsForAcc = [
                                ...(onlineProfile.otherProfileIds || []),
                                ...(onlineProfile.supportProfileIds || [])
                            ];

                            return competitorBrandIdsForAcc;
                        });

                    const competitorProfilesList = await asd(rootState.account.code, competitorOnlineProfileIds);
                    const ownProfilesList = await asd(rootState.account.code, ownOnlineProfileIds);

                    await commit('setCompetitorProfiles', competitorProfilesList);
                    await commit('setOwnProfiles', ownProfilesList);
                } catch(e) {
                    console.error(e);
                } finally {
                    refreshCompetitorProfilesPromise = null;
                }
            })();
            
            await refreshCompetitorProfilesPromise;
        },

        async refreshProfilesLatest({state, commit, dispatch, rootState}, forceRefresh) {
            forceRefresh ??= false;

            if (state.latestProfilesStats && !forceRefresh) return;

            if (refreshProfilesLatestPromise) {
                await refreshProfilesLatestPromise;
                return;
            }

            refreshProfilesLatestPromise = (async () => {
                try {
                await dispatch('refreshCompetitorProfiles', forceRefresh);

                const competitorProfiles = state.competitorProfiles || [];
                const ownProfiles = state.ownProfiles || [];

                const request = competitorProfiles.map(competitorProfile => competitorProfile.handleId);

                request.push(... ownProfiles.map(ownProfile => ownProfile.handleId));

                const bRes = await beansprout.post("/rest/profile-stats",
                    {handleIds: request});

                // enrich the beansprout OnlineProfileStats data with the onlineProfileId and channel
                bRes.data.map(profileStat => {
                    const find = competitorProfiles.find(handleId => handleId.handleId === profileStat.handle);
                    const findOwn = ownProfiles.find(handleId => handleId.handleId === profileStat.handle);

                    if (find) {
                        profileStat.brandId = find.brandId;
                        profileStat.channel = find.type;
                    } else if (findOwn) {
                        profileStat.brandId = findOwn.brandId;
                        profileStat.channel = findOwn.type;
                    }

                })

                    await commit('setLatestProfilesStats', bRes.data);
                } catch(e) {
                    console.error(e);
                } finally {
                    refreshProfilesLatestPromise = null;
                }
            })();
            
            await refreshProfilesLatestPromise;
        },
    }
}